import React, {useContext, useEffect, useRef, useState} from 'react'
import styles from '../../styles/dashboard.module.css'
import Header from "../../components/header/header";
import ListView from "./components/list_view";
import PageTitle from "../../components/page_title/page_title";
import Modal from "../../components/modal/modal";
import {TextInput, LogoInput} from "../../components/input/inputs";
import axiosInstance from "../../AxiosInstance";
import MyContext from "../../Context";
import EmptyPlaceholder from "../../components/empty_placeholder/empty_placeholder";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileImport, faFileExport} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from 'xlsx';
import {useTranslation} from 'react-i18next';

export default function Machines() {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [showModal, setShowModal] = useState(false);
    const [data, setData] = useState([]);
    const [form, setForm] = useState({project_id: project_id});
    const {setCompanyChange} = useContext(MyContext);
    const [activeTab, setActiveTab] = useState('machines');

    // Import state
    const fileInputRef = useRef(null);
    const [importDeltas, setImportDeltas] = useState([]);
    const [showImportPreview, setShowImportPreview] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [showImportResult, setShowImportResult] = useState(false);

    const fetchMachines = () => {
        if (!project_id) return;
        axiosInstance.get('device_user/?project_id=' + project_id).then((response) => {
            setData(response.data);
        });
    };

    useEffect(() => {
        setCompanyChange((prev) => prev + 1);
        setForm({
            ...form,
            project_id: project_id
        });

        fetchMachines();

        const intervalId = setInterval(fetchMachines, 10000);
        return () => clearInterval(intervalId);
    }, [project_id])

    const createAsset = () => {
        let formData = new FormData();
        for (const key in form) {
            formData.append(key, form[key]);
        }
        formData.set('is_human', activeTab === 'people' ? 'true' : 'false');
        axiosInstance.post('device_user/', formData).then(() => {
            window.location.reload();
        })
        setShowModal(false);
    }

    const people = data.filter(d => d.is_human);
    const machines = data.filter(d => !d.is_human);
    const filteredData = activeTab === 'people' ? people : machines;

    // --- Export ---
    const exportPeople = () => {
        const headers = [['Name', 'Identification Number', 'GPS Device SN']];
        const rows = people.map(p => [
            p.name || '',
            p.identification_number || '',
            p.active_gps_device_sn || '',
        ]);

        const ws = XLSX.utils.aoa_to_sheet([...headers, ...rows]);

        // Set column widths
        ws['!cols'] = [
            {wch: 25},
            {wch: 25},
            {wch: 20},
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'People');
        XLSX.writeFile(wb, 'people_export.xlsx');
    };

    // --- Import ---
    const handleImportFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, {type: 'array'});
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, {defval: ''});
            computeDeltas(rows);
            setShowImportPreview(true);
        };
        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    const computeDeltas = (rows) => {
        const existingByIdNumber = {};
        people.forEach(p => {
            if (p.identification_number) {
                existingByIdNumber[p.identification_number] = p;
            }
        });

        // Map header variations to internal keys
        const normalizeRow = (row) => {
            const normalized = {};
            for (const key of Object.keys(row)) {
                const k = key.toLowerCase().trim();
                if (k === 'name') normalized.name = row[key];
                else if (k.includes('identification') || k === 'id number' || k === 'id') normalized.identification_number = row[key];
                else if (k.includes('device') || k.includes('sn') || k.includes('serial')) normalized.device_sn = row[key];
            }
            return normalized;
        };

        const deltas = rows.map(row => {
            const nr = normalizeRow(row);
            const name = String(nr.name || '').trim();
            const idNum = String(nr.identification_number || '').trim();
            const deviceSn = String(nr.device_sn || '').trim();

            if (!name) {
                return {status: 'error', name, identification_number: idNum, device_sn: deviceSn, changes: 'Name is required'};
            }

            if (idNum && existingByIdNumber[idNum]) {
                const existing = existingByIdNumber[idNum];
                const changes = [];
                if (existing.name !== name) changes.push(`name: "${existing.name}" → "${name}"`);
                if ((existing.active_gps_device_sn || '') !== deviceSn && deviceSn) {
                    changes.push(`device_sn: "${existing.active_gps_device_sn || ''}" → "${deviceSn}"`);
                }

                if (changes.length === 0) {
                    return {status: 'unchanged', name, identification_number: idNum, device_sn: deviceSn, changes: ''};
                }
                return {status: 'update', name, identification_number: idNum, device_sn: deviceSn, changes: changes.join(', ')};
            }

            return {status: 'new', name, identification_number: idNum, device_sn: deviceSn, changes: ''};
        });

        setImportDeltas(deltas);
    };

    const confirmImport = () => {
        const rowsToSend = importDeltas
            .filter(d => d.status === 'new' || d.status === 'update')
            .map(d => ({
                name: d.name,
                identification_number: d.identification_number,
                device_sn: d.device_sn,
            }));

        if (rowsToSend.length === 0) {
            alert(t('resources.noChangesToImport'));
            return;
        }

        axiosInstance.post('device_user/bulk_import/', {
            project_id: project_id,
            rows: rowsToSend,
        }).then(res => {
            setShowImportPreview(false);
            setImportResult(res.data);
            setShowImportResult(true);
            fetchMachines();
        }).catch(() => {
            alert(t('resources.importFailed'));
        });
    };

    const newCount = importDeltas.filter(d => d.status === 'new').length;
    const updateCount = importDeltas.filter(d => d.status === 'update').length;
    const unchangedCount = importDeltas.filter(d => d.status === 'unchanged').length;
    const errorCount = importDeltas.filter(d => d.status === 'error').length;

    const statusBadge = (s) => {
        const badgeStyles = {
            new: {backgroundColor: '#DCFCE7', color: '#166534', label: t('resources.statusLabels.new')},
            update: {backgroundColor: '#FEF3C7', color: '#92400E', label: t('resources.statusLabels.update')},
            unchanged: {backgroundColor: '#F1F5F9', color: '#64748B', label: t('resources.statusLabels.unchanged')},
            error: {backgroundColor: '#FEE2E2', color: '#991B1B', label: t('resources.statusLabels.error')},
        };
        const st = badgeStyles[s] || badgeStyles.error;
        return (
            <span style={{
                padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                backgroundColor: st.backgroundColor, color: st.color,
            }}>{st.label}</span>
        );
    };

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={0}/>

            <Modal show={showModal} setter={setShowModal}
                   name={activeTab === 'people' ? t('resources.createPerson') : t('resources.createMachine')}
                   buttonName={t('common.ok')}
                   onClick={createAsset}>
                <div>
                    <LogoInput name={'Image'} internalName={'image_file'} form={form} setForm={setForm}
                               hideName={true}/>
                    <TextInput form={form} setForm={setForm} name={'name'}
                               placeholder={activeTab === 'people' ? t('resources.personName') : t('resources.machineName')}/>
                    {activeTab === 'people' && (
                        <TextInput form={form} setForm={setForm} name={'identification_number'}
                                   placeholder={t('resources.idNumber')}/>
                    )}
                </div>
            </Modal>

            {/* Import Preview Modal */}
            <Modal show={showImportPreview} setter={setShowImportPreview}
                   name={t('resources.importPreview')} buttonName={t('resources.confirmImport')} onClick={confirmImport}>
                <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
                    <div className="d-flex gap-3 flex-wrap" style={{marginBottom: '12px'}}>
                        {newCount > 0 && <span style={{fontSize: '13px', color: '#166534', fontWeight: '600'}}>{t('resources.new', {count: newCount})}</span>}
                        {updateCount > 0 && <span style={{fontSize: '13px', color: '#92400E', fontWeight: '600'}}>{t('resources.updates', {count: updateCount})}</span>}
                        {unchangedCount > 0 && <span style={{fontSize: '13px', color: '#64748B'}}>{t('resources.unchanged', {count: unchangedCount})}</span>}
                        {errorCount > 0 && <span style={{fontSize: '13px', color: '#991B1B', fontWeight: '600'}}>{t('resources.errors', {count: errorCount})}</span>}
                    </div>

                    {updateCount > 0 && (
                        <div style={{
                            padding: '8px 12px', backgroundColor: '#FEF3C7', borderRadius: '6px',
                            fontSize: '12px', color: '#92400E', marginBottom: '12px',
                        }}>
                            {t('resources.importNote')}
                        </div>
                    )}

                    <table style={{width: '100%', fontSize: '12px', borderCollapse: 'collapse'}}>
                        <thead>
                        <tr style={{borderBottom: '2px solid #E2E8F0'}}>
                            <th style={{padding: '6px', textAlign: 'left'}}>{t('resources.status')}</th>
                            <th style={{padding: '6px', textAlign: 'left'}}>{t('resources.name')}</th>
                            <th style={{padding: '6px', textAlign: 'left'}}>{t('resources.idNumber')}</th>
                            <th style={{padding: '6px', textAlign: 'left'}}>{t('resources.deviceSN')}</th>
                            <th style={{padding: '6px', textAlign: 'left'}}>{t('resources.changes')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {importDeltas.map((d, i) => (
                            <tr key={i} style={{borderBottom: '1px solid #F1F5F9'}}>
                                <td style={{padding: '6px'}}>{statusBadge(d.status)}</td>
                                <td style={{padding: '6px'}}>{d.name}</td>
                                <td style={{padding: '6px'}}>{d.identification_number}</td>
                                <td style={{padding: '6px'}}>{d.device_sn}</td>
                                <td style={{padding: '6px', fontSize: '11px', color: '#64748B'}}>{d.changes}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </Modal>

            {/* Import Result Modal */}
            <Modal show={showImportResult} setter={setShowImportResult} name={t('resources.importResult')} buttonName={t('common.ok')}
                   onClick={() => setShowImportResult(false)}>
                {importResult && (
                    <div>
                        <div style={{fontSize: '14px', marginBottom: '8px'}}>
                            <span style={{color: '#166534', fontWeight: '600'}}>{t('resources.created', {count: importResult.created})}</span>,{' '}
                            <span style={{color: '#92400E', fontWeight: '600'}}>{t('resources.updated', {count: importResult.updated})}</span>
                        </div>
                        {importResult.errors?.length > 0 && (
                            <div style={{marginTop: '8px'}}>
                                <div style={{fontSize: '13px', color: '#991B1B', fontWeight: '600', marginBottom: '4px'}}>{t('resources.errorsLabel')}</div>
                                {importResult.errors.map((err, i) => (
                                    <div key={i} style={{fontSize: '12px', color: '#991B1B'}}>
                                        {t('resources.row', {row: err.row, error: err.error})}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <input type="file" ref={fileInputRef} accept=".xlsx,.xls"
                   style={{display: 'none'}} onChange={handleImportFile}/>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('resources.title')} createName={activeTab === 'people' ? t('resources.person') : t('resources.machine')}
                           openModal={setShowModal}>
                    {activeTab === 'people' && (
                        <div style={{display: 'flex', gap: '8px', borderRight: '1px solid #CBD5E1', paddingRight: '16px', marginRight: '16px'}}>
                            <div className={`d-flex flex-row rounded-3`}
                                 style={{whiteSpace: 'nowrap', cursor: 'pointer', padding: '9px 18px', outline: '#C9D0D8 solid 1px'}}
                                 onClick={exportPeople}>
                                <FontAwesomeIcon icon={faFileExport} color="#64748B" style={{paddingRight: '10px', paddingTop: '4px'}}/>
                                <div style={{color: '#334155'}}>{t('common.export')}</div>
                            </div>
                            <div className={`d-flex flex-row rounded-3`}
                                 style={{whiteSpace: 'nowrap', cursor: 'pointer', padding: '9px 18px', outline: '#C9D0D8 solid 1px'}}
                                 onClick={() => fileInputRef.current?.click()}>
                                <FontAwesomeIcon icon={faFileImport} color="#64748B" style={{paddingRight: '10px', paddingTop: '4px'}}/>
                                <div style={{color: '#334155'}}>{t('common.import')}</div>
                            </div>
                        </div>
                    )}
                </PageTitle>

                {/* Tabs */}
                <div className={`d-flex flex-row rounded-3 mt-3 mb-3 ${styles.toggle}`} style={{width: 'fit-content'}}>
                    <div
                        className={activeTab === 'machines' ? styles.selected : styles.unselected}
                        style={{borderRadius: '6px 0 0 6px', cursor: 'pointer'}}
                        onClick={() => setActiveTab('machines')}
                    >
                        {t('resources.tabMachines')}
                    </div>
                    <div className={styles.separator}/>
                    <div
                        className={activeTab === 'people' ? styles.selected : styles.unselected}
                        style={{borderRadius: '0 6px 6px 0', cursor: 'pointer'}}
                        onClick={() => setActiveTab('people')}
                    >
                        {t('resources.tabPeople')}
                    </div>
                </div>

                {filteredData.length > 0 ?
                    <ListView data={filteredData} notDevice={true}/>
                    :
                    <EmptyPlaceholder type={'machines'}/>
                }
            </div>
        </div>
    )
}
